import { RefinementCtx, z, ZodNumber, ZodType } from "zod";
import type { RowValidationContext } from "view/agreements/validation/requiredUtils";
import { intl } from "LocaleWrapper";


export type RuleContext = (context: RowValidationContext) => boolean;

export type FieldRule = {
	hidden?: boolean;
	required?: boolean;
	hiddenIf?: RuleContext;
	requiredIf?: RuleContext;
}

export const throwError = (path: (string | number)[], ctx: RefinementCtx) => {
	ctx.addIssue({ code: "custom", message: intl.formatMessage({ id: "REQUIRED" }), path });
};

export const ZodSchema = {
	optionalNumber(min?: number, max?: number) {
		let s: ZodNumber = z.number();
		if (min !== undefined) {
			s = s.min(min);
		}
		if (max !== undefined) {
			s = s.max(max);
		}
		return s.nullish();
	},
	optionalInt(min?: number, max?: number) {
		let s: ZodNumber = z.number().int();
		if (min !== undefined) {
			s = s.min(min);
		}
		if (max !== undefined) {
			s = s.max(max);
		}
		return s.nullish();
	},

	/**
	 * **DO NOT CHANGE!**
	 *
	 * Adds a Zod transformer so that the inferred TS type of the schema is nullable, while being required upon validation.
	 *
	 * Usage:
	 * ```ts
	 * const CallInputSchema = z.object({
	 * 	id: z.null(),
	 * 	pta: requiredField(z.number().gt(0)),
	 * 	ptd: requiredField(z.number().gt(0)),
	 * 	location: requiredField(LocationSchema),
	 * });
	 *
	 * type CallInputFormType = z.input<typeof CallInputSchema>;
	 * type ValidatedCallInputFormType = z.output<typeof CallInputSchema>;
	 * ```
	 * */
	requiredField: <T extends ZodType,>(schema: T, message?: string) => {
		return schema.nullable().transform((val, ctx) => {
			if (val === null) {
				ctx.addIssue({
					code: "custom",
					fatal: true,
					message: message ?? intl.formatMessage({ id: "REQUIRED" }),
				});

				return z.NEVER;
			}

			return val;
		});
	},
};
